import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a list of numeric values into integer percentages that sum to exactly 100.
 * Uses the largest remainder method to distribute rounding errors.
 * Returns an empty array if the input is empty or the total is zero.
 */
export function toPercentages(values: number[]): number[] {
  if (values.length === 0) return [];

  const total = values.reduce((sum, v) => sum + v, 0);
  if (total === 0) return values.map(() => 0);

  const exact = values.map((v) => (v / total) * 100);
  const floored = exact.map(Math.floor);
  const remainder = 100 - floored.reduce((sum, v) => sum + v, 0);

  // Distribute remaining points to the items with the largest fractional parts
  const indices = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac)
    .slice(0, remainder)
    .map(({ i }) => i);

  return floored.map((v, i) => (indices.includes(i) ? v + 1 : v));
}
